<template>
  <v-container fluid class="pa-0 d-flex flex-column" style="height: 100vh; overflow: hidden;">
    <!-- Template Management Dialog -->
    <v-dialog v-model="showTemplateDialog" max-width="800px">
      <v-card>
        <v-card-title>
          <span v-if="loadingTemplates">Loading Templates...</span>
          <span v-else>Select an Email Template</span>
          <v-spacer/>
          <v-btn icon @click="showTemplateDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider/>
        <v-card-text style="max-height: 500px; overflow-y: auto;">
          <v-btn
            block
            color="primary"
            class="mb-3"
            @click="createNewTemplate"
          >
            <v-icon left>mdi-plus</v-icon>
            New Template
          </v-btn>
          <v-checkbox
            v-model="showDeletedTemplates"
            label="Show deleted templates"
            dense
            hide-details
            class="mt-0 mb-3"
            @change="loadTemplates"
          />
          <v-list v-if="!loadingTemplates && templates.length > 0">
            <v-list-item
              v-for="template in templates"
              :key="template.id"
              :class="{
                'v-list-item--active': currentTemplateId === template.id,
                'deleted-template': template.deleted
              }"
            >
              <v-list-item-content @click="!template.deleted && loadTemplate(template.id)">
                <v-list-item-title>
                  {{ template.id }}
                  <v-chip v-if="template.deleted" x-small color="error" class="ml-2">Deleted</v-chip>
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ template.description }}
                </v-list-item-subtitle>
                <v-list-item-subtitle class="text-caption">
                  Updated: {{ formatDate(template.updatedAt) }}
                  <span v-if="template.deleted">
                    • Deleted: {{ formatDate(template.deletedAt) }}
                    <span v-if="template.deletedBy"> by {{ template.deletedBy }}</span>
                  </span>
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action v-if="template.deleted && !readOnly">
                <v-btn
                  small
                  outlined
                  color="success"
                  @click.stop="restoreTemplate(template.id)"
                >
                  <v-icon small left>mdi-restore</v-icon>
                  Restore
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
          <v-alert v-else-if="!loadingTemplates" type="info" text>
            No templates found. Create your first template!
          </v-alert>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="500px">
      <v-card>
        <v-card-title class="error white--text">
          Confirm Delete
          <v-spacer/>
          <v-btn icon dark @click="showDeleteDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider/>
        <v-card-text class="pa-4">
          <p class="mb-0">Are you sure you want to delete this template?</p>
          <p class="mb-0 mt-2"><strong>{{ currentTemplateId }}</strong></p>
          <p class="text-caption grey--text mt-2">The template will be marked as deleted and can be restored later.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn text @click="showDeleteDialog = false">Cancel</v-btn>
          <v-btn color="error" @click="confirmDelete" :loading="deleting">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Send Test Email Dialog -->
    <v-dialog v-model="showSendTestDialog" max-width="500px">
      <v-card>
        <v-card-title>
          Send Test Email
          <v-spacer/>
          <v-btn icon @click="showSendTestDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider/>
        <v-card-text class="pa-3">
          <div class="text-subtitle-2">Customer data</div>
          <p class="text-caption grey--text mb-1">
            The email is rendered with this customer's real data, the way it would be sent to them.
            Leave empty to use the "{{ activeSampleDataName }}" sample data instead.
          </p>
          <customer-autocomplete v-model="testCustomer"/>
          <v-chip v-if="testCustomer" small close class="mb-2" @click:close="testCustomer = null">
            {{ testCustomer.first_name }} {{ testCustomer.last_name }} · {{ testCustomer.email }}
          </v-chip>
          <v-text-field
            v-if="testCustomer"
            v-model="testEmailDate"
            label="Delivery date (optional, for date-driven emails)"
            type="date"
            outlined
            dense
            hide-details
            class="mb-3"
          />
          <div class="text-subtitle-2 mt-2">Send to</div>
          <p class="text-caption grey--text mb-1">
            Test emails go to you, never to the customer.
          </p>
          <v-text-field
            v-model="testEmailAddress"
            label="Email Address"
            type="email"
            outlined
            dense
            placeholder="your@email.com"
            :error-messages="testEmailError"
          />
          <v-alert v-if="testEmailSuccess" type="success" dense class="mt-2">
            Test email sent to {{ testEmailSentTo }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn outlined @click="showSendTestDialog = false">Cancel</v-btn>
          <v-btn
            color="primary"
            @click="sendTestEmail"
            :loading="sendingTestEmail"
            :disabled="!testEmailAddress"
          >
            <v-icon small left>mdi-send</v-icon>
            Send
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Version History Dialog -->
    <v-dialog v-model="showVersionDialog" max-width="600px">
      <v-card>
        <v-card-title>
          Version History
          <v-spacer/>
          <v-btn icon @click="showVersionDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider/>
        <v-card-text style="max-height: 500px; overflow-y: auto;">
          <div v-if="versions.length > 0">
            <!-- Active Version Section -->
            <div v-if="activeVersion" class="mb-4">
              <div class="text-subtitle-2 mb-2 d-flex align-center">
                <v-icon small color="success" class="mr-1">mdi-check-circle</v-icon>
                Active Version
              </div>
              <v-list>
                <v-list-item
                  v-for="version in versions.filter(v => v.versionId === activeVersion)"
                  :key="'active-' + version.versionId"
                  @click="loadVersion(version.versionId)"
                  color="success"
                  class="active-version-item"
                >
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ formatDate(version.createdAt) }}
                      <v-chip x-small color="success" class="ml-2">Active</v-chip>
                    </v-list-item-title>
                    <v-list-item-subtitle>
                      Created by {{ version.createdBy }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </div>

            <!-- All Versions Section -->
            <div class="text-subtitle-2 mb-2">All Versions</div>
            <v-list>
              <v-list-item
                v-for="version in versions"
                :key="version.versionId"
                @click="loadVersion(version.versionId)"
                :class="{'v-list-item--active': currentVersion === version.versionId}"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    {{ formatDate(version.createdAt) }}
                    <v-chip v-if="version.versionId === activeVersion" x-small color="success" class="ml-2">Active
                    </v-chip>
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    Created by {{ version.createdBy }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
          <v-alert v-else type="info" text>
            No version history available.
          </v-alert>
        </v-card-text>
      </v-card>
    </v-dialog>

    <!-- Sample Data Dialog -->
    <v-dialog v-model="showSampleDataDialog" max-width="800px">
      <v-card style="display: flex; flex-direction: column;">
        <v-card-title class="py-2">
          Sample Data (JSON)
          <v-spacer/>
          <v-select
            v-model="activeSampleDataIndex"
            :items="sampleDataSets.map((set, i) => ({ text: set.name, value: i }))"
            dense
            outlined
            hide-details
            style="max-width: 200px;"
            class="mr-2"
          />
          <v-btn v-if="!readOnly" small outlined @click="addSampleDataSet" class="mr-2">
            <v-icon small left>mdi-plus</v-icon>
            Add
          </v-btn>
          <v-btn
            v-if="!readOnly"
            small
            outlined
            color="error"
            @click="deleteSampleDataSet"
            :disabled="sampleDataSets.length === 1"
            class="mr-2"
          >
            <v-icon small>mdi-delete</v-icon>
          </v-btn>
          <v-btn icon @click="showSampleDataDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider/>
        <v-card-text class="pa-2 pb-0">
          <v-text-field
            v-model="sampleDataSets[activeSampleDataIndex].name"
            label="Data Set Name"
            dense
            outlined
            hide-details
            class="mb-2"
            :disabled="readOnly"
          />
        </v-card-text>
        <v-card-text class="pa-2 flex-grow-1" style="overflow: hidden; height: 100%">
          <codemirror
            v-if="showSampleDataDialog"
            ref="jsonEditor"
            :key="activeSampleDataIndex"
            v-model="sampleDataSets[activeSampleDataIndex].data"
            :options="readOnly ? { ...jsonEditorOptions, readOnly: true } : jsonEditorOptions"
            @input="debouncedCompile"
            style="height: 100%;"
          />
          <v-alert v-if="dataError" type="error" dense class="mt-2 mb-0">
            {{ dataError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="primary" @click="showSampleDataDialog = false" :disabled="!!dataError">
            Done
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-row no-gutters class="flex-shrink-0"
           style="position: sticky; top: 0; background: white; border-bottom: 1px solid #e0e0e0;">
      <v-col :cols="readOnly ? 12 : 6" class="">
        <v-card flat>
          <v-card-actions class="py-2">
            <v-chip v-if="compiling" small color="primary" outlined class="mr-2">
              <v-icon small left>mdi-refresh</v-icon>
              Compiling...
            </v-chip>
            <v-btn small outlined @click="showTemplateDialog = true" class="mr-1">
              <v-icon small left>mdi-folder-open</v-icon>
              Open
            </v-btn>
            <v-btn
              v-if="canEdit && !editing"
              small
              outlined
              color="primary"
              @click="editing = true"
              class="mr-1"
            >
              <v-icon small left>mdi-pencil</v-icon>
              Edit
            </v-btn>
            <v-btn
              v-if="editing"
              small
              outlined
              @click="hasUnsavedChanges ? confirmDoneEditing() : editing = false"
              class="mr-1"
            >
              <v-icon small left>mdi-pencil-off</v-icon>
              Done
            </v-btn>
            <v-btn
              v-if="!readOnly && currentTemplateId && isEditingExisting"
              small
              outlined
              color="error"
              @click="showDeleteDialog = true"
              class="mr-1"
            >
              <v-icon small left>mdi-delete</v-icon>
              Delete
            </v-btn>
            <v-btn
              v-if="!readOnly"
              small
              outlined
              @click="editGlobalStyles"
              color="accent"
              :loading="loadingGlobalStyles"
            >
              <v-icon small left>mdi-palette</v-icon>
              Styles
            </v-btn>
            <v-spacer/>
            <!-- Preview controls shown inline when read-only (right col is hidden) -->
            <template v-if="readOnly">
              <v-btn small outlined color="primary" @click="showSendTestDialog = true" class="mr-2"
                     :disabled="!!compileError">
                <v-icon small left>mdi-send</v-icon>
                Send Test
              </v-btn>
              <v-spacer/>
              <v-select
                v-model="activeSampleDataIndex"
                :items="sampleDataSets.map((set, i) => ({ text: set.name, value: i }))"
                label="Preview Data"
                dense
                outlined
                hide-details
                style="max-width: 180px;"
                class="mr-1"
                @change="compileTemplate"
              />
              <v-btn small outlined @click="showSampleDataDialog = true" class="mr-2" icon title="View sample data">
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
              <v-btn-toggle v-model="viewportMode" mandatory dense class="mr-2">
                <v-btn small value="desktop">
                  <v-icon small>mdi-monitor</v-icon>
                </v-btn>
                <v-btn small value="mobile">
                  <v-icon small>mdi-cellphone</v-icon>
                </v-btn>
              </v-btn-toggle>
              <v-btn small :outlined="!darkMode" :color="darkMode ? 'grey darken-3' : ''" :dark="darkMode"
                     @click="darkMode = !darkMode" class="mr-2" title="Toggle dark mode preview">
                <v-icon small>mdi-weather-night</v-icon>
              </v-btn>
              <v-btn small :outlined="previewMode === 'rendered'" :color="previewMode === 'html' ? 'grey darken-2' : ''"
                     :dark="previewMode === 'html'"
                     @click="previewMode = previewMode === 'rendered' ? 'html' : 'rendered'"
                     class="mr-2" title="Toggle HTML source">
                <v-icon small>mdi-code-tags</v-icon>
              </v-btn>
            </template>
            <v-btn v-if="!readOnly && hasUnsavedChanges"
                   small
                   :outlined="!hasUnsavedChanges"
                   :color="hasUnsavedChanges ? 'error' : 'primary'"
                   @click="saveTemplate"
                   :loading="saving"
                   class="mr-1 save-button"
                   :class="{ 'unsaved-changes': hasUnsavedChanges }"
            >
              <v-icon small left>mdi-content-save</v-icon>
              {{ !isEditingExisting || !currentVersion ? 'Save' : 'Save Draft' }}
            </v-btn>
            <v-chip v-if="!hasUnsavedChanges && isActive" small color="success" class="mr-2">
              <v-icon small left>mdi-check-circle</v-icon>
              Published Version
            </v-chip>
            <v-btn
              v-if="!readOnly && currentTemplateId && currentVersion && (hasUnsavedChanges || !isActive)"
              small
              outlined
              color="success"
              @click="hasUnsavedChanges ? saveAndMakeActive() : setAsActive()"
              :loading="settingActive || saving"
            >
              <v-icon small left>mdi-check</v-icon>
              {{ hasUnsavedChanges ? 'Save and Publish' : 'Publish' }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
      <v-col v-if="!readOnly" cols="6">
        <v-card flat>
          <v-card-title class="py-2">
            <v-select
              v-model="activeSampleDataIndex"
              :items="sampleDataSets.map((set, i) => ({ text: set.name, value: i }))"
              label="Preview Data"
              dense
              outlined
              hide-details
              style="max-width: 180px;"
              class="ml-3 mr-1"
              @change="compileTemplate"
            />
            <v-btn v-if="!readOnly" small outlined @click="showSampleDataDialog = true" class="mr-2" icon
                   :title="'Edit sample data'">
              <v-icon small>mdi-pencil</v-icon>
            </v-btn>
            <v-btn v-if="readOnly" small outlined @click="showSampleDataDialog = true" class="mr-2" icon
                   :title="'View sample data'">
              <v-icon small>mdi-eye</v-icon>
            </v-btn>
            <v-spacer/>
            <v-btn-toggle v-model="viewportMode" mandatory dense class="mr-2">
              <v-btn small value="desktop">
                <v-icon small>mdi-monitor</v-icon>
              </v-btn>
              <v-btn small value="mobile">
                <v-icon small>mdi-cellphone</v-icon>
              </v-btn>
            </v-btn-toggle>
            <v-btn
              small
              :outlined="!darkMode"
              :color="darkMode ? 'grey darken-3' : ''"
              :dark="darkMode"
              @click="darkMode = !darkMode"
              class="mr-2"
              title="Toggle dark mode preview"
            >
              <v-icon small>mdi-weather-night</v-icon>
            </v-btn>
            <v-btn
              v-if="!readOnly"
              small
              outlined
              color="primary"
              @click="showSendTestDialog = true"
              class="mr-2"
              :disabled="!!compileError"
            >
              <v-icon small left>mdi-send</v-icon>
              Send Test
            </v-btn>
            <v-btn
              small
              :outlined="previewMode === 'rendered'"
              :color="previewMode === 'html' ? 'grey darken-2' : ''"
              :dark="previewMode === 'html'"
              @click="previewMode = previewMode === 'rendered' ? 'html' : 'rendered'"
              class="mr-2"
              title="Toggle HTML source"
            >
              <v-icon small>mdi-code-tags</v-icon>
            </v-btn>
          </v-card-title>
        </v-card>
      </v-col>
    </v-row>
    <v-row no-gutters class="flex-grow-1" style="min-height: 0; height:100%; overflow: hidden;">
      <!-- Left Panel - Source Code Editor -->
      <v-col v-if="!readOnly" cols="6" class="d-flex flex-column" style="height: 100%;">
        <v-card flat class="d-flex flex-column" style="height: 100%;">
          <!-- Top Controls Section -->
          <v-card-text class="pa-2">
            <v-row dense>
              <v-col cols="6">
                <v-text-field
                  v-model="currentTemplateId"
                  label="Template ID"
                  dense
                  outlined
                  :hide-details="!idErrors.length"
                  :disabled="isEditingExisting"
                  :error-messages="idErrors"
                  @input="validateId"
                  @blur="validateId"
                  placeholder="e.g., welcome-email"
                  required
                />
              </v-col>
              <v-col cols="6" class="d-flex align-center">
                <template v-if="currentTemplateId && versions.length > 0">
                  <v-select
                    v-if="showVersionSelect"
                    style="max-width: 100%;"
                    v-model="currentVersion"
                    :items="filteredVersions.map(v => ({
                      text: `${formatDate(v.createdAt)} • ${v.createdBy}${v.versionId === activeVersion ? ' ⭐' : ''}`,
                      value: v.versionId
                    }))"
                    label="Version"
                    dense
                    outlined
                    hide-details
                    class="mr-2"
                    @change="loadVersion"
                  >
                    <template v-slot:prepend-item v-if="versions.length > 10">
                      <v-list-item>
                        <v-list-item-content>
                          <v-checkbox
                            v-model="showAllVersions"
                            :label="`Show all ${versions.length} versions`"
                            dense
                            hide-details
                            class="mt-0"
                          />
                        </v-list-item-content>
                      </v-list-item>
                      <v-divider class="mb-2"/>
                    </template>
                  </v-select>
                  <span v-else class="text-caption grey--text">
                    {{ formatDate(currentVersionDate) }}
                    <v-chip v-if="isActive" x-small color="success" class="ml-1">Active</v-chip>
                  </span>
                  <v-btn icon class="ml-1" :title="showVersionSelect ? 'Hide versions' : 'Switch version'"
                         @click="showVersionSelect = !showVersionSelect">
                    <v-icon>mdi-history</v-icon>
                  </v-btn>
                </template>
              </v-col>
              <v-col cols="12">
                <v-text-field
                  v-model="templateDescription"
                  label="Description"
                  dense
                  outlined
                  :hide-details="!descriptionErrors.length"
                  :error-messages="descriptionErrors"
                  @input="validateDescription"
                  @blur="validateDescription"
                  placeholder="Template description..."
                  required
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="emailSubject"
                  label="Email Subject"
                  dense
                  outlined
                  :hide-details="!subjectErrors.length"
                  :error-messages="subjectErrors"
                  @input="validateSubject"
                  @blur="validateSubject"
                  :placeholder="`e.g., Welcome {{name}}!`"
                  @keyup="()=>debouncedCompile(1000)"
                  required
                />
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="fromAddress"
                  label="From Address"
                  dense
                  outlined
                  :hide-details="!fromAddressErrors.length"
                  :error-messages="fromAddressErrors"
                  @input="validateFromAddress"
                  @blur="validateFromAddress"
                  type="email"
                  placeholder="e.g., support@fedfedfed.com"
                  required
                />
              </v-col>
            </v-row>
          </v-card-text>


          <!-- MJML Editor - Full Height -->
          <v-card-text class="flex-grow-1 pa-2" style="overflow: hidden;">
            <codemirror
              ref="mjmlEditor"
              v-model="templateSource"
              :options="mjmlEditorOptions"
              @input="debouncedCompile"
              style="height: 100%;"
            />
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Read-Only Info Panel -->
      <v-col v-if="readOnly" cols="12" class="pa-2">
        <v-card outlined>
          <v-card-text class="pb-2">
            <v-row dense>
              <v-col cols="12" md="3">
                <div class="text-caption grey--text">Template ID</div>
                <div class="text-body-1">{{ currentTemplateId || 'N/A' }}</div>
              </v-col>
              <v-col cols="12" md="3">
                <div class="text-caption grey--text">Version</div>
                <div class="d-flex align-center">
                  <template v-if="currentTemplateId && versions.length > 0">
                    <v-select
                      v-if="showVersionSelect"
                      v-model="currentVersion"
                      :items="filteredVersions.map(v => ({
                        text: `${formatDate(v.createdAt)} • ${v.createdBy}${v.versionId === activeVersion ? ' ⭐' : ''}`,
                        value: v.versionId
                      }))"
                      dense
                      outlined
                      hide-details
                      @change="loadVersion"
                    >
                      <template v-slot:prepend-item v-if="versions.length > 10">
                        <v-list-item>
                          <v-list-item-content>
                            <v-checkbox
                              v-model="showAllVersions"
                              :label="`Show all ${versions.length} versions`"
                              dense
                              hide-details
                              class="mt-0"
                            />
                          </v-list-item-content>
                        </v-list-item>
                        <v-divider class="mb-2"/>
                      </template>
                    </v-select>
                    <span v-else class="text-body-1">
                      {{ currentVersionDate ? formatDate(currentVersionDate) : 'N/A' }}
                      <v-chip v-if="isActive" x-small color="success" class="ml-1">Active</v-chip>
                    </span>
                    <v-btn icon class="ml-1" :title="showVersionSelect ? 'Hide versions' : 'Switch version'"
                           @click="showVersionSelect = !showVersionSelect">
                      <v-icon>mdi-history</v-icon>
                    </v-btn>
                  </template>
                  <span v-else class="text-body-1">N/A</span>
                </div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="text-caption grey--text">Description</div>
                <div class="text-body-1">{{ templateDescription || 'N/A' }}</div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="text-caption grey--text">Email Subject</div>
                <div class="text-body-1">{{ emailSubject || 'N/A' }}</div>
              </v-col>
              <v-col cols="12" md="6">
                <div class="text-caption grey--text">From Address</div>
                <div class="text-body-1">{{ fromAddress || 'N/A' }}</div>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>

      <v-divider v-if="!readOnly" vertical/>

      <!-- Right Panel - Preview -->
      <v-col :cols="readOnly ? 12 : 6" class="d-flex flex-column" style="height: 100%;">
        <v-card flat class="d-flex flex-column" style="height: 100%;">
          <v-card-title>Subject: {{ compiledEmailSubject }}</v-card-title>
          <v-divider/>
          <v-card-text class="flex-grow-1 pa-0 d-flex flex-column" style="overflow: hidden;">
            <div v-if="previewMode === 'rendered'" class="preview-container flex-grow-1"
                 :style="`overflow: auto; display: flex; justify-content: center; align-items: flex-start; background: ${darkMode ? '#1a1a1a' : '#f5f5f5'};`">
              <div :class="viewportMode === 'mobile' ? 'mobile-viewport' : 'desktop-viewport'">
                <iframe
                  ref="previewFrame"
                  :srcdoc="previewHtml"
                  frameborder="0"
                  :style="`width: 100%; min-height: 100%; background: ${darkMode ? '#1a1a1a' : 'white'};`"
                />
              </div>
            </div>

            <div v-else class="pa-2 flex-grow-1" style="overflow: auto;">
              <pre class="code-output">{{ compiledHtml }}</pre>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import mjml2html from 'mjml-browser';
import Handlebars from 'handlebars';
import api from '../api';
import CustomerAutocomplete from '@/components/CustomerAutocomplete.vue';

// CodeMirror imports
import {codemirror} from 'vue-codemirror';
import 'codemirror/lib/codemirror.css';
import 'codemirror/theme/material.css';
import 'codemirror/mode/xml/xml.js';
import 'codemirror/mode/javascript/javascript.js';
import 'codemirror/addon/edit/closetag.js';
import 'codemirror/addon/edit/matchbrackets.js';
import 'codemirror/addon/fold/foldcode.js';
import 'codemirror/addon/fold/foldgutter.js';
import 'codemirror/addon/fold/foldgutter.css';
import 'codemirror/addon/fold/xml-fold.js';
import 'codemirror/addon/display/placeholder.js';
import urlState from "../router/urlState";

function getDefaultTemplate() {
  return `<mjml>
  <mj-body>
    <mj-section>
      <mj-column>
        <mj-text font-size="20px" color="#626262" font-family="Helvetica">
          Hello {{name}}!
        </mj-text>
        <mj-text font-size="16px" color="#525252">
          Welcome to our email template editor.
        </mj-text>
        {{#if items}}
        <mj-text font-size="14px">
          <strong>Your items:</strong>
          <ul>
            {{#each items}}
            <li>{{this}}</li>
            {{/each}}
          </ul>
        </mj-text>
        {{/if}}
        <mj-button href="https://example.com" background-color="#f45e43">
          Click Me
        </mj-button>
      </mj-column>
    </mj-section>
  </mj-body>
</mjml>`;
}

function getDefaultData() {
  return {
    name: 'Default',
    data: `{
  "name": "John Doe",
  "items": ["Item 1", "Item 2", "Item 3"]
}`,
  };
}

export default {
  name: 'EmailTemplateEditor',

  components: {
    codemirror,
    CustomerAutocomplete,
  },

  data() {
    return {
      templateSource: getDefaultTemplate(),
      sampleDataSets: [
        getDefaultData(),
      ],
      activeSampleDataIndex: 0,
      compiledHtml: '',
      compiledEmailSubject: '',
      compileError: null,
      compileWarnings: null,
      dataError: null,
      compiling: false,
      previewMode: 'rendered',
      debounceTimer: null,
      // Template management
      currentTemplateId: '',
      currentVersion: null,
      currentVersionDate: null,
      activeVersion: null,
      isActive: false,
      settingActive: false,
      templateDescription: '',
      emailSubject: '',
      fromAddress: 'support@fedfedfed.com',
      idErrors: [],
      descriptionErrors: [],
      subjectErrors: [],
      fromAddressErrors: [],
      isEditingExisting: false,
      templates: [],
      versions: [],
      showTemplateDialog: false,
      showVersionDialog: false,
      showSampleDataDialog: false,
      showSendTestDialog: false,
      showDeleteDialog: false,
      loadingTemplates: false,
      showDeletedTemplates: false,
      deleting: false,
      saving: false,
      // Send test email
      testEmailAddress: '',
      testEmailError: null,
      testEmailSuccess: false,
      testEmailSentTo: '',
      testCustomer: null,
      testEmailDate: '',
      sendingTestEmail: false,
      // Global styles
      globalStyles: null,
      loadingGlobalStyles: false,
      // Track last saved state
      lastSavedState: null,
      // CodeMirror options
      mjmlEditorOptions: {
        tabSize: 2,
        mode: 'text/html',
        theme: 'material',
        lineNumbers: true,
        line: true,
        autoCloseTags: true,
        matchBrackets: true,
        foldGutter: true,
        gutters: ['CodeMirror-linenumbers', 'CodeMirror-foldgutter', 'CodeMirror-lint-markers'],
        extraKeys: {
          'Ctrl-Space': 'autocomplete',
        },
        placeholder: 'Enter MJML template with Handlebars syntax...',
      },
      jsonEditorOptions: {
        tabSize: 2,
        mode: {
          name: 'javascript',
          json: true,
        },
        theme: 'material',
        lineNumbers: true,
        line: true,
        autoCloseTags: true,
        matchBrackets: true,
        fixedGutter: true,
        foldGutter: true,
        gutters: ["CodeMirror-linenumbers", "CodeMirror-foldgutter"],
        placeholder: '{"name": "John", "items": []}',
      },
      // Track line markers for cleanup
      lineMarkers: [],
      lineWidgets: [],
      // Preview viewport mode
      viewportMode: 'desktop', // 'desktop' or 'mobile'
      // Dark mode preview
      darkMode: false,
      // Version filtering
      showAllVersions: false,
      showVersionSelect: false,
      editing: false,
    };
  },
  mixins: [urlState],
  computed: {
    canEdit() {
      return !this.$route.meta.readOnly;
    },
    activeSampleDataName() {
      return this.sampleDataSets?.[this.activeSampleDataIndex]?.name || 'Default';
    },
    readOnly() {
      return !this.editing || !!this.$route.meta.readOnly;
    },
    filteredVersions() {
      if (!this.versions || this.versions.length === 0) return [];

      // Always include the active version and current version
      const mustInclude = new Set([this.activeVersion, this.currentVersion].filter(Boolean));

      if (this.showAllVersions) {
        return this.versions;
      }

      // Show only the most recent 10 versions, but always include active and current
      const recent = this.versions.slice(0, 10);
      const recentIds = new Set(recent.map(v => v.versionId));

      // Add any must-include versions that aren't in the recent list
      const additional = this.versions.filter(v =>
        mustInclude.has(v.versionId) && !recentIds.has(v.versionId)
      );

      return [...recent, ...additional];
    },
    hasUnsavedChanges() {
      // If no saved state and we have content, there are unsaved changes (new template)
      if (!this.lastSavedState) {
        return !!(this.currentTemplateId);
      }
      if (!this.sampleDataSets || this.sampleDataSets.length === 0) return false;

      return (
        this.emailSubject !== this.lastSavedState.emailSubject ||
        this.fromAddress !== this.lastSavedState.fromAddress ||
        this.currentTemplateId !== this.lastSavedState.id ||
        this.templateDescription !== this.lastSavedState.description ||
        this.templateSource !== this.lastSavedState.templateSource ||
        JSON.stringify(this.sampleDataSets) !== JSON.stringify(this.lastSavedState.sampleDataSets)
      );
    },
    previewHtml() {
      if (!this.compiledHtml || !this.darkMode) return this.compiledHtml;
      // Inject a style block that forces dark color-scheme so email clients' dark mode
      // media queries and color-scheme declarations are activated.
      const darkStyle = `<style>
        :root { color-scheme: dark; }
        html, body { background-color: #1a1a1a !important; }
      </style>`;
      return this.compiledHtml.replace('</head>', darkStyle + '</head>');
    },
  },

  mounted() {
    this.ensureSampleDataSets();
    this.loadGlobalStyles();
    this.loadTemplates();
    this.compileTemplate();

    this.syncToUrl({
      param: 'currentTemplateId',
      urlParam: 'template',
      initFromRoute: true,
    });
    this.syncToUrl({
      param: 'currentVersion',
      urlParam: 'version',
      initFromRoute: true,
    });
    if (this.currentTemplateId) {
      this.loadTemplate(this.currentTemplateId, this.currentVersion);
    } else {
      this.showTemplateDialog = true;
    }
  },

  methods: {
    confirmDoneEditing() {
      if (confirm('You have unsaved changes. Leave without saving?')) {
        this.editing = false;
      }
    },
    async loadGlobalStyles() {
      try {
        this.loadingGlobalStyles = true;
        const response = await api.get('/v2/email-templates/global-styles', {params: {useActive: 'true'}});
        this.globalStyles = response.data.templateSource;
      } catch (error) {
        // Global styles don't exist yet, that's okay
        if (error.response?.status !== 404) {
          console.error('Failed to load global styles:', error);
        }
        this.globalStyles = null;
      } finally {
        this.loadingGlobalStyles = false;
      }
    },

    async editGlobalStyles() {
      try {
        // Try to load the existing global-styles template
        await this.loadTemplate('global-styles');
        this.$store.commit('showSnackbar', {
          message: 'Editing global styles. These will be auto-prepended to all templates.',
          color: 'info',
        });
      } catch (error) {
        // Global styles don't exist yet, create new
        this.currentTemplateId = 'global-styles';
        this.currentVersion = null;
        this.currentVersionDate = null;
        this.activeVersion = null;
        this.isActive = false;
        this.templateDescription = 'Global styles and mj-classes that are automatically prepended to all templates';
        this.emailSubject = '';
        this.fromAddress = 'support@fedfedfed.com';
        this.isEditingExisting = false;

        // Default global styles template
        this.templateSource = `<mjml>
  <mj-head>
    <mj-attributes>
      <!-- Define your global mj-classes here -->
      <mj-class name="primary-button" background-color="#1976d2" color="white" font-weight="bold" />
      <mj-class name="secondary-button" background-color="#f5f5f5" color="#333" />
      <mj-class name="heading" font-size="24px" font-weight="bold" color="#333" />
      <mj-class name="subheading" font-size="18px" color="#666" />
    </mj-attributes>

    <mj-style>
      /* Add your global CSS here */
      .text-center { text-align: center; }
    </mj-style>
  </mj-head>
  <mj-body>
    <!-- This body won't be used, only the head section is extracted -->
  </mj-body>
</mjml>`;

        this.sampleDataSets = [getDefaultData()];
        this.activeSampleDataIndex = 0;
        this.lastSavedState = null;

        this.compileTemplate();
        this.$store.commit('showSnackbar', {
          message: 'Editing global styles. These will be auto-prepended to all templates.',
          color: 'info',
        });
      }
    },
    createNewTemplate() {
      // Clear all fields for a new template
      this.currentTemplateId = '';
      this.currentVersion = null;
      this.currentVersionDate = null;
      this.activeVersion = null;
      this.isActive = false;
      this.templateDescription = '';
      this.emailSubject = '';
      this.fromAddress = 'support@fedfedfed.com';
      this.templateSource = getDefaultTemplate();
      this.sampleDataSets = [getDefaultData()];
      this.activeSampleDataIndex = 0;
      this.isEditingExisting = false;
      this.versions = [];
      this.idErrors = [];
      this.descriptionErrors = [];
      this.lastSavedState = null;

      // Close the dialog
      this.showTemplateDialog = false;

      // Recompile
      this.compileTemplate();

      this.$store.commit('showSnackbar', {
        message: 'Ready to create new template',
        color: 'info',
      });
    },

    async loadTemplates() {
      try {
        this.loadingTemplates = true;
        const params = this.showDeletedTemplates ? {includeDeleted: 'true'} : {};
        const response = await api.get('/v2/email-templates', {params});
        this.templates = response.data;
      } catch (error) {
        console.error('Failed to load templates:', error);
        this.$store.commit('showSnackbar', {
          message: 'Failed to load templates',
          color: 'error',
        });
      } finally {
        this.loadingTemplates = false;
      }
    },

    async loadTemplate(templateId, version = null) {
      try {
        const response = version
          ? await api.get(`/v2/email-templates/${templateId}?version=${version}`)
          : await api.get(`/v2/email-templates/${templateId}`, {params: {useActive: 'true'}})
        const template = response.data;

        this.currentTemplateId = template.id;
        this.currentVersion = template.version;
        this.currentVersionDate = template.versionCreatedAt;
        this.activeVersion = template.activeVersion;
        this.isActive = template.isActive || false;
        this.templateDescription = template.description;
        this.emailSubject = template.emailSubject || '';
        this.fromAddress = template.fromAddress || 'support@fedfedfed.com';
        this.templateSource = template.templateSource;
        this.sampleDataSets = template.sampleDataSets && template.sampleDataSets.length > 0
          ? template.sampleDataSets
          : [getDefaultData()];
        this.activeSampleDataIndex = 0;
        this.idErrors = [];
        this.descriptionErrors = [];
        this.subjectErrors = [];
        this.fromAddressErrors = [];
        this.isEditingExisting = true;

        this.ensureSampleDataSets();

        // Update last saved state
        this.lastSavedState = {
          id: this.currentTemplateId,
          description: this.templateDescription,
          emailSubject: this.emailSubject,
          fromAddress: this.fromAddress,
          templateSource: this.templateSource,
          sampleDataSets: JSON.parse(JSON.stringify(this.sampleDataSets)),
        };

        this.showTemplateDialog = false;

        // Load version history for the dropdown
        await this.loadVersionHistory();

        this.compileTemplate();

        this.$store.commit('showSnackbar', {
          message: `Loaded template: ${template.id}`,
          color: 'success',
        });
      } catch (error) {
        console.error('Failed to load template:', error);
        this.$store.commit('showSnackbar', {
          message: 'Failed to load template',
          color: 'error',
        });
      }
    },

    validateId() {
      this.idErrors = [];

      if (!this.currentTemplateId || !this.currentTemplateId.trim()) {
        this.idErrors = ['Template ID is required'];
        return;
      }

      const idPattern = /^[a-zA-Z0-9-]+$/;
      if (!idPattern.test(this.currentTemplateId)) {
        this.idErrors = ['Template ID can only contain letters, numbers, and dashes (no spaces)'];
      }
    },

    validateDescription() {
      this.descriptionErrors = [];

      const desc = this.templateDescription || '';
      if (!desc.trim()) {
        this.descriptionErrors = ['Description is required'];
      }
    },

    validateSubject() {
      this.subjectErrors = [];

      const subject = this.emailSubject || '';
      if (!subject.trim()) {
        this.subjectErrors = ['Email subject is required'];
      }
    },

    validateFromAddress() {
      this.fromAddressErrors = [];

      const address = this.fromAddress || '';
      if (!address.trim()) {
        this.fromAddressErrors = ['From address is required'];
        return;
      }

      // Basic email validation
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(address.trim())) {
        this.fromAddressErrors = ['Please enter a valid email address'];
      }
    },

    async saveTemplate() {
      let success = false;
      // Validate all fields
      this.validateId();
      this.validateDescription();
      this.validateSubject();
      this.validateFromAddress();

      // Check if there are any validation errors
      const hasErrors = this.idErrors.length > 0 ||
        this.descriptionErrors.length > 0 ||
        this.subjectErrors.length > 0 ||
        this.fromAddressErrors.length > 0;

      if (hasErrors) {
        this.$store.commit('showSnackbar', {
          message: 'Please fix the validation errors',
          color: 'warning',
        });
        return;
      }

      // Check for duplicate template ID when saving a new template
      if (!this.currentVersion) {
        try {
          await api.get(`/v2/email-templates/${this.currentTemplateId}`);
          // If we get here, the template already exists
          this.$store.commit('showSnackbar', {
            message: `Template ID "${this.currentTemplateId}" already exists. Please choose a different ID.`,
            color: 'error',
          });
          this.idErrors = ['This template ID is already in use'];
          return false;
        } catch (error) {
          // 404 means template doesn't exist, which is what we want for a new template
          if (error.response?.status !== 404) {
            // Some other error occurred
            console.error('Error checking for duplicate template:', error);
            this.$store.commit('showSnackbar', {
              message: 'Failed to check for duplicate template ID',
              color: 'error',
            });
            return false;
          }
          // 404 is expected - template doesn't exist, we can proceed
        }
      }

      try {
        this.saving = true;

        // Create merged version with global styles
        let mergedSource = this.templateSource;
        if (this.globalStyles && this.currentTemplateId !== 'global-styles') {
          mergedSource = this.mergeGlobalStyles(this.templateSource);
        }

        const payload = {
          id: this.currentTemplateId,
          description: this.templateDescription,
          emailSubject: this.emailSubject,
          fromAddress: this.fromAddress,
          templateSource: this.templateSource,
          mergedSource: mergedSource,
          sampleDataSets: this.sampleDataSets,
        };

        const response = await api.post('/v2/email-templates', payload);
        const saved = response.data;
        success = true;

        this.currentTemplateId = saved.id;
        this.currentVersion = saved.version;
        this.currentVersionDate = saved.versionCreatedAt;
        this.activeVersion = saved.activeVersion;
        this.isActive = saved.isActive || false;
        this.isEditingExisting = true;

        // Update last saved state
        this.lastSavedState = {
          id: this.currentTemplateId,
          description: this.templateDescription,
          emailSubject: this.emailSubject,
          fromAddress: this.fromAddress,
          templateSource: this.templateSource,
          sampleDataSets: JSON.parse(JSON.stringify(this.sampleDataSets)),
        };

        // If we just saved global styles, reload them
        if (saved.id === 'global-styles') {
          await this.loadGlobalStyles();
        }

        this.$store.commit('showSnackbar', {
          message: `Template saved successfully! Version: ${saved.version}`,
          color: 'success',
        });

        // Reload templates list and version history
        await this.loadTemplates();
        await this.loadVersionHistory();
      } catch (error) {
        console.error('Failed to save template:', error);
        this.$store.commit('showSnackbar', {
          message: 'Failed to save template',
          color: 'error',
        });
        success = false;
      } finally {
        this.saving = false;
      }
      return success;
    },

    async loadVersionHistory() {
      if (!this.currentTemplateId) return;

      try {
        const response = await api.get(`/v2/email-templates/${this.currentTemplateId}/versions`);
        this.versions = response.data;
      } catch (error) {
        console.error('Failed to load version history:', error);
        this.$store.commit('showSnackbar', {
          message: 'Failed to load version history',
          color: 'error',
        });
      }
    },

    async loadVersion(versionId) {
      try {
        const response = await api.get(`/v2/email-templates/${this.currentTemplateId}/versions/${versionId}`);
        const version = response.data;

        this.templateSource = version.templateSource;
        this.sampleDataSets = version.sampleDataSets && version.sampleDataSets.length > 0
          ? version.sampleDataSets
          : [getDefaultData()];
        this.activeSampleDataIndex = 0;
        this.currentVersion = version.versionId;
        this.currentVersionDate = version.versionCreatedAt;
        this.activeVersion = version.activeVersion;
        this.isActive = version.isActive || false;
        this.emailSubject = version.emailSubject || '';
        this.fromAddress = version.fromAddress || 'support@fedfedfed.com';
        this.ensureSampleDataSets();

        // Update last saved state
        this.lastSavedState = {
          id: this.currentTemplateId,
          description: this.templateDescription,
          emailSubject: this.emailSubject,
          fromAddress: this.fromAddress,
          templateSource: this.templateSource,
          sampleDataSets: JSON.parse(JSON.stringify(this.sampleDataSets)),
        };

        this.showVersionDialog = false;
        this.compileTemplate();

        this.$store.commit('showSnackbar', {
          message: `Loaded version: ${versionId}`,
          color: 'success',
        });
      } catch (error) {
        console.error('Failed to load version:', error);
        this.$store.commit('showSnackbar', {
          message: 'Failed to load version',
          color: 'error',
        });
      }
    },

    async setAsActive() {
      try {
        this.settingActive = true;
        await api.post(`/v2/email-templates/${this.currentTemplateId}/versions/${this.currentVersion}/set-active`);

        this.activeVersion = this.currentVersion;
        this.isActive = true;

        this.$store.commit('showSnackbar', {
          message: 'Version published',
          color: 'success',
        });
      } catch (error) {
        console.error('Failed to set active version:', error);
        this.$store.commit('showSnackbar', {
          message: 'Failed to publish version',
          color: 'error',
        });
      } finally {
        this.settingActive = false;
      }
    },

    async saveAndMakeActive() {
      try {
        this.settingActive = true;

        // First save the template
        const saved = await this.saveTemplate();

        if (saved) {
          // If save was successful, currentVersion will be updated to the new version
          // Now set it as active
          if (this.currentVersion) {
            await api.post(`/v2/email-templates/${this.currentTemplateId}/versions/${this.currentVersion}/set-active`);

            this.activeVersion = this.currentVersion;
            this.isActive = true;

            this.$store.commit('showSnackbar', {
              message: 'Template saved and published',
              color: 'success',
            });
          }
        }
      } catch (error) {
        console.error('Failed to save and set active:', error);
        this.$store.commit('showSnackbar', {
          message: 'Failed to save and publish',
          color: 'error',
        });
      } finally {
        this.settingActive = false;
      }
    },

    async confirmDelete() {
      try {
        this.deleting = true;
        await api.delete(`/v2/email-templates/${this.currentTemplateId}`);

        this.showDeleteDialog = false;

        // Clear the current template
        this.currentTemplateId = '';
        this.currentVersion = null;
        this.currentVersionDate = null;
        this.activeVersion = null;
        this.isActive = false;
        this.templateDescription = '';
        this.emailSubject = '';
        this.fromAddress = 'support@fedfedfed.com';
        this.templateSource = getDefaultTemplate();
        this.sampleDataSets = [getDefaultData()];
        this.activeSampleDataIndex = 0;
        this.versions = [];
        this.idErrors = [];
        this.descriptionErrors = [];
        this.subjectErrors = [];
        this.fromAddressErrors = [];
        this.isEditingExisting = false;
        this.lastSavedState = null;

        this.$store.commit('showSnackbar', {
          message: 'Template deleted successfully',
          color: 'success',
        });

        // Reload templates list to reflect the deletion
        await this.loadTemplates();

        this.compileTemplate();
      } catch (error) {
        console.error('Failed to delete template:', error);
        this.$store.commit('showSnackbar', {
          message: 'Failed to delete template',
          color: 'error',
        });
      } finally {
        this.deleting = false;
      }
    },

    async restoreTemplate(templateId) {
      try {
        await api.post(`/v2/email-templates/${templateId}/restore`);

        this.$store.commit('showSnackbar', {
          message: 'Template restored successfully',
          color: 'success',
        });

        await this.loadTemplates();
      } catch (error) {
        console.error('Failed to restore template:', error);
        this.$store.commit('showSnackbar', {
          message: 'Failed to restore template',
          color: 'error',
        });
      }
    },

    formatDate(isoString) {
      if (!isoString) return '';
      const date = new Date(isoString);
      return date.toLocaleString();
    },


    async sendTestEmail() {
      this.testEmailError = null;
      this.testEmailSuccess = false;

      // Validate email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(this.testEmailAddress)) {
        this.testEmailError = 'Please enter a valid email address';
        return;
      }

      try {
        this.sendingTestEmail = true;

        // the server renders the source being edited (unsaved changes included)
        // with the fed-lib email definition's data for the chosen customer, or
        // with the active sample data when no customer is chosen
        const payload = {
          templateId: this.currentTemplateId,
          templateSource: this.templateSource,
          emailSubject: this.emailSubject,
          fromAddress: this.fromAddress,
          to: this.testEmailAddress,
        };
        if (this.testCustomer) {
          payload.uid = this.testCustomer.uid;
          if (this.testEmailDate) payload.date = this.testEmailDate;
        } else {
          payload.sampleData = JSON.parse(this.sampleDataSets?.[this.activeSampleDataIndex]?.data || '{}');
        }

        const response = await api.post('/v2/email-templates/send-test', payload);

        this.testEmailSentTo = response.data?.to || this.testEmailAddress;
        this.testEmailSuccess = true;

        setTimeout(() => {
          this.showSendTestDialog = false;
          this.testEmailSuccess = false;
        }, 2000);

      } catch (error) {
        console.error('Failed to send test email:', error);
        this.testEmailError = error.response?.data?.error || 'Failed to send test email';
      } finally {
        this.sendingTestEmail = false;
      }
    },

    mergeGlobalStyles(templateSource) {
      // Extract <mj-head> content from global styles
      const globalStylesMatch = this.globalStyles.match(/<mj-head>([\s\S]*?)<\/mj-head>/);
      if (!globalStylesMatch) return templateSource;

      const globalHeadContent = globalStylesMatch[1];

      // Check if template has <mj-head>
      const templateHeadMatch = templateSource.match(/<mj-head>([\s\S]*?)<\/mj-head>/);

      if (templateHeadMatch) {
        // Template has <mj-head>, merge content
        const mergedHead = `<mj-head>${globalHeadContent}${templateHeadMatch[1]}</mj-head>`;
        return templateSource.replace(/<mj-head>[\s\S]*?<\/mj-head>/, mergedHead);
      } else {
        // Template doesn't have <mj-head>, insert after <mjml>
        return templateSource.replace(/<mjml>/, `<mjml>\n  <mj-head>${globalHeadContent}</mj-head>`);
      }
    },
    debouncedCompile(timeout = 500) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = setTimeout(() => {
        this.compileTemplate();
      }, timeout);
    },

    clearEditorMarkers() {
      const cm = this.$refs.mjmlEditor?.codemirror;
      if (!cm) return;

      // Clear line class markers
      this.lineMarkers.forEach(({line, severity}) => {
        cm.removeLineClass(line, 'background', `line-${severity}`);
      });
      this.lineMarkers = [];

      // Clear all widgets
      this.lineWidgets.forEach(widget => widget.clear());
      this.lineWidgets = [];

      // Clear gutter markers
      cm.clearGutter('CodeMirror-lint-markers');
    },

    escapeHtml(text) {
      const div = document.createElement('div');
      div.textContent = text;
      return div.innerHTML;
    },

    checkTemplateErrors(templateSource, data) {
      try {
        // Compile Handlebars first
        let mjmlSource;
        try {
          const handlebarsTemplate = Handlebars.compile(templateSource);
          mjmlSource = handlebarsTemplate(data);
        } catch (handlebarsError) {
          console.warn('Handlebars compilation error:', handlebarsError);

          // Extract line number from Handlebars error
          // Handlebars errors typically include line numbers in the message or as a property
          let lineNumber = null;

          if (handlebarsError.lineNumber) {
            lineNumber = handlebarsError.lineNumber;
          } else if (handlebarsError.message) {
            // Try to parse line number from error message
            // Format: "Parse error on line X:" or similar
            const lineMatch = handlebarsError.message.match(/line (\d+)/i);
            if (lineMatch) {
              lineNumber = parseInt(lineMatch[1], 10);
            }
          }

          if (lineNumber) {
            this.addEditorError(lineNumber, `Handlebars error: ${handlebarsError.message}`, 'error');
          }

          return;
        }

        // Compile MJML to check for errors (without global styles)
        const result = mjml2html(mjmlSource, {
          validationLevel: 'soft',
        });

        // Handle errors and warnings
        if (result.errors && result.errors.length > 0) {
          console.log('MJML errors:', result.errors);
          result.errors.forEach(err => {
            // Check if we have a valid line number
            if (err.line && err.line > 0) {
              const isError = err.formattedMessage && err.formattedMessage.includes('error');
              const severity = isError ? 'error' : 'warning';
              this.addEditorError(err.line, err.message, severity);
            } else {
              console.warn('MJML error without line number:', err);
            }
          });
        }
      } catch (error) {
        // If error checking fails, just log it and continue
        console.warn('Error checking template errors:', error);
      }
    },

    addEditorError(line, message, severity = 'error') {
      const cm = this.$refs.mjmlEditor?.codemirror;
      if (!cm) {
        console.warn('CodeMirror instance not found');
        return;
      }

      const lineNumber = line - 1; // CodeMirror uses 0-based line numbers
      console.log(`Adding ${severity} marker at line ${line} (${lineNumber}):`, message);

      // Validate line number
      if (lineNumber < 0 || lineNumber >= cm.lineCount()) {
        console.warn(`Invalid line number ${line} (converted to ${lineNumber}), total lines: ${cm.lineCount()}`);
        return;
      }

      // Add gutter marker
      const marker = document.createElement('div');
      marker.className = `lint-marker-${severity}`;
      marker.title = message;
      marker.innerHTML = severity === 'error' ? '●' : '▲';
      cm.setGutterMarker(lineNumber, 'CodeMirror-lint-markers', marker);

      // Highlight the line - store info for later removal
      cm.addLineClass(lineNumber, 'background', `line-${severity}`);
      this.lineMarkers.push({line: lineNumber, severity});

      // Add line widget with message
      const widget = document.createElement('div');
      widget.className = `lint-message lint-message-${severity}`;
      widget.textContent = message;
      const lineWidget = cm.addLineWidget(lineNumber, widget, {
        coverGutter: false,
        noHScroll: false,
      });
      this.lineWidgets.push(lineWidget);
    },

    ensureSampleDataSets() {
      if (!this.sampleDataSets || this.sampleDataSets.length === 0) {
        this.sampleDataSets = [getDefaultData()];
        this.activeSampleDataIndex = 0;
      }
    },

    addSampleDataSet() {
      this.ensureSampleDataSets();
      const newIndex = this.sampleDataSets.length;
      const currentData = this.sampleDataSets[this.activeSampleDataIndex]?.data || '{}';
      this.sampleDataSets.push({
        name: `Sample Data ${newIndex + 1}`,
        data: currentData,
      });
      this.activeSampleDataIndex = newIndex;
      this.compileTemplate();
    },

    deleteSampleDataSet() {
      this.ensureSampleDataSets();
      if (this.sampleDataSets.length === 1) return;

      this.sampleDataSets.splice(this.activeSampleDataIndex, 1);
      this.activeSampleDataIndex = Math.min(
        this.activeSampleDataIndex,
        this.sampleDataSets.length - 1
      );
      this.compileTemplate();
    },

    compileTemplate() {
      this.compiling = true;
      this.compileError = null;
      this.compileWarnings = null;
      this.dataError = null;

      // Clear previous markers
      this.clearEditorMarkers();

      try {
        // Ensure sample data sets exist
        this.ensureSampleDataSets();

        // Parse sample data from active set
        let data = {};
        const activeSet = this.sampleDataSets?.[this.activeSampleDataIndex];
        const activeData = activeSet?.data || '{}';
        if (activeData.trim()) {
          try {
            data = JSON.parse(activeData);
          } catch (e) {
            this.dataError = `Invalid JSON: ${e.message}`;
            this.compiling = false;
            return;
          }
        }

        // Compile email subject with Handlebars
        if (this.emailSubject) {
          try {
            // plain text, as in fed-lib's compileSubject
            const subjectTemplate = Handlebars.compile(this.emailSubject, {noEscape: true});
            this.compiledEmailSubject = subjectTemplate(data);
          } catch (e) {
            this.compiledEmailSubject = this.emailSubject; // Fallback to raw subject
          }
        } else {
          this.compiledEmailSubject = '';
        }

        // Check for errors in the un-merged template (matches editor content)
        // Skip if editing global-styles since it's not a complete MJML document
        if (this.currentTemplateId !== 'global-styles') {
          this.checkTemplateErrors(this.templateSource, data);
        }

        // Step 1: Compile Handlebars template
        let mjmlSource;
        try {
          const handlebarsTemplate = Handlebars.compile(this.templateSource);
          mjmlSource = handlebarsTemplate(data);
        } catch (handlebarsError) {
          console.error('Handlebars compilation error for preview:', handlebarsError);
          this.compiledHtml = `
            <!DOCTYPE html>
            <html>
              <head>
                <style>
                  body { font-family: Arial, sans-serif; padding: 20px; background: #f8d7da; }
                  h2 { color: #721c24; margin-top: 0; }
                  pre { background: white; padding: 20px; border-left: 4px solid #dc3545; overflow-x: auto; white-space: pre-wrap; }
                </style>
              </head>
              <body>
                <h2>❌ Handlebars Compilation Failed</h2>
                <pre>${this.escapeHtml(handlebarsError.message || String(handlebarsError))}</pre>
              </body>
            </html>
          `;
          return;
        }

        // Step 1.5: Prepend global styles if available and not editing global styles
        if (this.globalStyles && this.currentTemplateId !== 'global-styles') {
          mjmlSource = this.mergeGlobalStyles(mjmlSource);
        }

        // Step 2: Compile MJML to HTML for preview
        try {
          const result = mjml2html(mjmlSource, {
            validationLevel: 'soft',
          });

          // Check if there are critical errors in the merged compilation
          if (result.errors && result.errors.length > 0) {
            const criticalErrors = result.errors.filter(err =>
              err.formattedMessage && err.formattedMessage.toLowerCase().includes('error')
            );

            if (criticalErrors.length > 0) {
              // Replace HTML with error message
              const errorList = criticalErrors.map(err =>
                `<li><strong>Line ${err.line || '?'}:</strong> ${this.escapeHtml(err.message)}</li>`
              ).join('');

              this.compiledHtml = `
                <!DOCTYPE html>
                <html>
                  <head>
                    <style>
                      body { font-family: Arial, sans-serif; padding: 20px; background: #fff3cd; }
                      h2 { color: #856404; margin-top: 0; }
                      ul { background: white; padding: 20px; border-left: 4px solid #ffc107; }
                      li { margin: 10px 0; color: #333; }
                    </style>
                  </head>
                  <body>
                    <h2>⚠️ MJML Compilation Errors</h2>
                    <ul>${errorList}</ul>
                  </body>
                </html>
              `;
            } else {
              // Only warnings, show the HTML
              this.compiledHtml = result.html;
            }
          } else {
            this.compiledHtml = result.html;
          }
        } catch (error) {
          // Handle compilation exceptions
          console.error('MJML compilation exception:', error);
          this.compiledHtml = `
            <!DOCTYPE html>
            <html>
              <head>
                <style>
                  body { font-family: Arial, sans-serif; padding: 20px; background: #f8d7da; }
                  h2 { color: #721c24; margin-top: 0; }
                  pre { background: white; padding: 20px; border-left: 4px solid #dc3545; overflow-x: auto; }
                </style>
              </head>
              <body>
                <h2>❌ MJML Compilation Failed</h2>
                <pre>${this.escapeHtml(error.message || String(error))}</pre>
              </body>
            </html>
          `;
        }

        // Update iframe height after render
        this.$nextTick(() => {
          if (this.$refs.previewFrame) {
            this.$refs.previewFrame.onload = () => {
              try {
                const iframeDoc = this.$refs.previewFrame.contentDocument;
                if (iframeDoc && iframeDoc.body) {
                  const height = iframeDoc.body.scrollHeight;
                  this.$refs.previewFrame.style.height = height + 'px';
                }
              } catch (e) {
                console.error('Error adjusting iframe height:', e);
              }
            };
          }
        });

      } catch (error) {
        this.compileError = error.message;
        console.error('Compilation error:', error);
      } finally {
        this.compiling = false;
      }
    },
  },

  watch: {
    showSendTestDialog(open) {
      // tests go to the signed-in staff member unless they type another address
      if (open && !this.testEmailAddress) {
        this.testEmailAddress = this.$store.getters.getCurrentUser?.email || '';
      }
      if (open) {
        this.testEmailError = null;
        this.testEmailSuccess = false;
      }
    },
    async showSampleDataDialog(val) {
      // editor.refresh() is needed to recompute gutters/etc because editor was hidden
      await this.$nextTick();
      if (val) {
        const editor = this.$refs.jsonEditor;
        setTimeout(() => editor.refresh(), 100);
      }
    },
    showVersionDialog(val) {
      if (val) {
        this.loadVersionHistory();
      }
    },
  },

  beforeDestroy() {
    clearTimeout(this.debounceTimer);
  },
};
</script>

<style>
/* CodeMirror styling */
.vue-codemirror {
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
}

.CodeMirror {
  height: 100%;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
  font-size: 13px;
}

.CodeMirror-gutters {
  background-color: #263238;
  border-right: 1px solid #37474f;
}

/* Lint markers in gutter */
.CodeMirror-lint-markers {
  width: 16px;
}

.lint-marker-error {
  color: #ff5252;
  cursor: help;
  font-size: 16px;
  line-height: 1;
}

.lint-marker-warning {
  color: #ffc107;
  cursor: help;
  font-size: 14px;
  line-height: 1;
}

/* Line highlighting */
.line-error {
  background-color: rgba(255, 82, 82, 0.15) !important;
}

.line-warning {
  background-color: rgba(255, 193, 7, 0.15) !important;
}

/* Line widgets with messages */
.lint-message {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', monospace;
  font-size: 12px;
  padding: 4px 8px;
  margin: 2px 0;
  border-left: 3px solid;
}

.lint-message-error {
  background-color: rgba(255, 82, 82, 0.1);
  border-left-color: #ff5252;
  color: #ff5252;
}

.lint-message-warning {
  background-color: rgba(255, 193, 7, 0.1);
  border-left-color: #ffc107;
  color: #ff8f00;
}


.code-output {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 12px;
  background: #f8f8f8;
  padding: 12px;
  border-radius: 4px;
  overflow-x: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.preview-container {
  width: 100%;
  background: #f5f5f5;
}

.fill-height {
  height: 100vh;
}

/* Viewport modes for preview */
.desktop-viewport {
  width: 100%;
  min-height: 100%;
}

.mobile-viewport {
  width: 375px;
  min-height: 600px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border-radius: 8px;
  overflow: hidden;
  margin: 16px;
}

/* Active version highlight */
.active-version-item {
  background-color: rgba(76, 175, 80, 0.08);
  border-left: 3px solid #4caf50;
}

/* Deleted template styling */
.deleted-template {
  opacity: 0.6;
}

.deleted-template .v-list-item__content {
  text-decoration: line-through;
}

/* Make sure scoped styles don't interfere */
</style>
